import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

import { useHubReactions } from "@/hooks/hub/useHubReactions";
import type { HubReactionCounts } from "@/lib/hub/reactions";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useHubSettings } from "@/hooks/hub/useHubSettings";
import { useHubIdentities } from "@/hooks/hub/useHubIdentities";
import { useHubFeed, type HubPost } from "@/hooks/hub/useHubFeed";
import { useHubFeeds } from "@/hooks/hub/useHubFeeds";
import { useHubPinnedPost } from "@/hooks/hub/useHubPinnedPost";
import { HubComposer, type HubComposerSubmit } from "@/components/social/HubComposer";
import { HubPostCard, HubPendingPostCard } from "@/components/social/HubPostCard";
import type { HubAuthor } from "@/components/social/HubAuthorDisplay";

/**
 * LR HUB center feed: composer + optimistic posting + newest-first list.
 *
 * POSTING: the composer submits to the `moderate-hub-post` edge function,
 * the ONLY write path for hub_posts (no client insert grant/policy exists).
 * The optimistic card is appended to the top of the feed immediately, then
 * either settles as the confirmed row, stays visible as "in review" (flag),
 * or is removed with the reason shown in the composer (block).
 *
 * ISOLATION: reads hub_posts / hub_settings only.
 */

export function HubFeed({
  prefill,
  onPrefillApplied,
  feedId = null,
  feedSlug = null,
  focusPostId = null,
  onFocusHandled,
}: {
  prefill?: { body: string; content_type: string } | null;
  onPrefillApplied?: () => void;
  /** null = Home (all feeds mixed); a feed id filters to that single feed. */
  feedId?: string | null;
  feedSlug?: string | null;
  /** Post to scroll to / highlight (set from the Recent Activity rail box). */
  focusPostId?: string | null;
  onFocusHandled?: () => void;
}) {
  const { user } = useAuth();
  const { data: mySettings } = useHubSettings();
  const feed = useHubFeed(feedId);
  const { byId } = useHubFeeds();
  // Single-feed views float that feed's pinned post to the top (Home does not).
  const pin = useHubPinnedPost(feedId);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<{ tone: "error" | "info"; text: string } | null>(null);

  // Scroll a requested post into view once it is rendered (retries briefly
  // while the first page settles), then clear the highlight.
  useEffect(() => {
    if (!focusPostId) return;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const attempt = () => {
      const el = document.getElementById(`hub-post-${focusPostId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        timer = setTimeout(() => onFocusHandled?.(), 2000);
        return;
      }
      if (tries++ < 20) timer = setTimeout(attempt, 200);
      else onFocusHandled?.();
    };
    attempt();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPostId]);


  const [justPostedId, setJustPostedId] = useState<string | null>(null);
  const [savingEditId, setSavingEditId] = useState<string | null>(null);


  const pinnedPost = pin.pinned;
  // De-dupe: the pinned post is rendered above the list, never twice.
  const listPosts = pinnedPost
    ? feed.posts.filter((p) => p.id !== pinnedPost.id)
    : feed.posts;
  const renderedPosts = pinnedPost ? [pinnedPost, ...listPosts] : listPosts;

  const identities = useHubIdentities([
    ...renderedPosts.map((p) => p.author_id),
    user?.id,
  ]);

  const reactions = useHubReactions(renderedPosts.map((p) => p.id));

  const afterPinChange = useCallback(() => {
    pin.refetch();
    feed.refetch();
  }, [pin, feed]);

  const authorFor = useCallback(
    (post: HubPost): HubAuthor => {
      const identity = identities.map.get(post.author_id) ?? null;
      return {
        id: post.author_id,
        name: identity?.display_name || "LR Member",
        hubIdentity: identity,
      };
    },
    [identities.map],
  );

  const meAuthor: HubAuthor = {
    id: user?.id,
    name: mySettings?.display_name || "You",
    hubIdentity: user?.id ? (identities.map.get(user.id) ?? null) : null,
  };

  const submit = useCallback(
    async (input: HubComposerSubmit) => {
      const tempId = `tmp-${Date.now()}`;
      setNotice(null);
      setSubmitting(true);
      // OPTIMISTIC: the post appears at the top of the feed right away.
      feed.addPending({
        tempId,
        body: input.body,
        content_type: input.content_type,
        tags: [],
        feed_slug: input.feed_slug,
        feed_name: input.feed_name,
        created_at: new Date().toISOString(),
      });

      try {
        const { data, error } = await supabase.functions.invoke("moderate-hub-post", {
          body: input,
        });
        if (error) throw error;

        if (data?.verdict === "block") {
          feed.removePending(tempId);
          setNotice({
            tone: "error",
            text: data.reason || "This post doesn't meet the LR Hub guidelines.",
          });
          return;
        }

        if (data?.post) {
          const settled = data.post as HubPost;
          feed.settlePending(tempId, settled);
          // Micro-animation: the confirmed post fades/slides in at the top.
          setJustPostedId(settled.id);
          window.setTimeout(() => setJustPostedId((id) => (id === settled.id ? null : id)), 900);
          if (data.verdict === "flag") {
            setNotice({
              tone: "info",
              text: data.reason || "Your post is in review and will go live once approved.",
            });
          }
          return;
        }

        feed.failPending(tempId, data?.error || "Could not post. Try again.");
      } catch (e) {
        feed.failPending(tempId, e instanceof Error ? e.message : "Could not post. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [feed],
  );

  /**
   * EDIT: the new body is sent back through `moderate-hub-post` with the
   * post id — the function re-moderates it and verifies ownership server-side.
   */
  const saveEdit = useCallback(
    async (postId: string, body: string): Promise<boolean> => {
      setNotice(null);
      setSavingEditId(postId);
      try {
        const { data, error } = await supabase.functions.invoke("moderate-hub-post", {
          body: { post_id: postId, body },
        });
        if (error) throw error;

        if (data?.verdict === "block") {
          setNotice({
            tone: "error",
            text: data.reason || "This post doesn't meet the LR Hub guidelines.",
          });
          return false;
        }
        if (data?.post) {
          const saved = data.post as HubPost;
          // Guard: if the function ever answers with a DIFFERENT row (e.g. an
          // outdated deploy that inserted instead of updating), don't silently
          // leave the original text on screen — surface it and resync.
          if (saved.id !== postId) {
            feed.refetch();
            setNotice({
              tone: "error",
              text: "Your edit couldn't be applied to this post. Refresh and try again.",
            });
            return false;
          }
          feed.patchPost(saved);
          if (data.verdict === "flag") {
            setNotice({
              tone: "info",
              text: data.reason || "Your edit is in review and will go live once approved.",
            });
          }
          return true;
        }

        setNotice({ tone: "error", text: data?.error || "Could not save your changes." });
        return false;
      } catch (e) {
        setNotice({
          tone: "error",
          text: e instanceof Error ? e.message : "Could not save your changes.",
        });
        return false;
      } finally {
        setSavingEditId(null);
      }
    },
    [feed],
  );


  return (
    <>
      <HubComposer
        currentFeedSlug={feedSlug}
        onSubmit={submit}
        submitting={submitting}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        prefill={prefill}
        onPrefillApplied={onPrefillApplied}
      />

      {feed.pending.map((p) => (
        <HubPendingPostCard
          key={p.tempId}
          post={p}
          author={meAuthor}
          onDismiss={() => feed.removePending(p.tempId)}
        />
      ))}

      {feed.isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : feed.isError ? (
        <p className="rounded-xl border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          Could not load the feed. Refresh to try again.
        </p>
      ) : renderedPosts.length === 0 && feed.pending.length === 0 ? (
        <p className="rounded-xl border border-border bg-card p-8 text-center text-[12px] text-muted-foreground">
          No posts yet — share a capacity update or a lane need to get things moving.
        </p>
      ) : null}

      {renderedPosts.map((p, i) => (
        <div
          key={p.id}
          id={`hub-post-${p.id}`}
          className={cn(
            "scroll-mt-4 rounded-xl transition-shadow",
            focusPostId === p.id && "ring-2 ring-brand-teal/60",
          )}
        >
          <HubPostCard
            onPinChanged={afterPinChange}
            post={p}
            author={authorFor(p)}
            feedLabel={p.feed?.name ?? (p.feed_id ? byId.get(p.feed_id)?.name : null) ?? null}
            justPosted={justPostedId === p.id}
            isOwn={!!user?.id && p.author_id === user.id}
            saving={savingEditId === p.id}
            onEditSave={(body) => saveEdit(p.id, body)}
            onDeleted={() => feed.removePost(p.id)}
            reactions={{
              mine: reactions.mineFor(p.id),
              counts: reactions.countsFor(p.id, (p.reaction_counts ?? {}) as HubReactionCounts),
              canReact: reactions.canReact,
              onReact: (type) => reactions.react(p.id, type),
            }}
            className={i % 2 === 1 ? "bg-[#fafbfc]" : undefined}
          />
        </div>
      ))}



      {feed.hasMore ? (
        <div className="flex justify-center pb-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-border text-[11.5px] font-bold"
            disabled={feed.isFetchingMore}
            onClick={() => feed.loadMore()}
          >
            {feed.isFetchingMore ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}

export default HubFeed;
